import { EditableText, defineBlock } from "eddev/blocks"
import { Container, Grid, Col } from "@components/layout/Grid"
import { cn } from "@utils/tw"
import { Card } from "@components/molecules/Cards"
import { ComponentPropsWithoutRef, forwardRef, Fragment } from "react"
import { DocumentCardFragment } from "../../types.graphql"
import { Button } from "@components/atoms/Button"
import { useBreakpointIsFrom, useIsMobile } from "@hooks/useViewportDerived"

export const meta: BlockMeta = {
  title: "Hero document highlight",
  category: "common",
  icon: "media-document",
  keywords: ["document"],
  tags: ["root"],
  editMode: "preview",
  lazyLoad: false,
  conditions: {
    postTypes: ["page", "post"],
  },
}

export default defineBlock("home/hero-document-highlight", (props) => {
  const isLaptop = useBreakpointIsFrom("laptop")
  const isMobile = useIsMobile()

  return (
    <Container
      className={cn(
        "bg-light-grey rounded-md p-6 my-6",
        "[&_h5]:m-0 [&_h5]:!type-label-caps [&_h5]:text-dark-grey",
        "[&_h2]:m-0",
        // stitches' "* > p" selector, and its `typography: "type-m"` value,
        // matched no real typography key (a typo for "text-m") — the global
        // `p` element rule already applies type-body-m/150% line-height, so
        // this only ever contributed `margin: 0` visually.
        "[&_p]:m-0",
      )}
    >
      <div className="grid w-full grid-cols-12 gap-6">
        <Col initial={12} tablet={6} laptop={5}>
          <Content>
            <div>
              <EditableText as="h5" store="subtitle" placeholder="Enter subtitle..." />
              <EditableText as="h2" className="!type-title-m" store="title" placeholder="Enter title..." />
              <EditableText as="p" store="content" placeholder="Enter content..." />
            </div>
            {!isMobile && props.button && (
              <Button
                size="medium"
                color="yellow"
                href={props.button.url!}
                target={props.button.target ? "_blank" : undefined}
              >
                {props.button.title}
              </Button>
            )}
          </Content>
        </Col>

        {isLaptop && <Col initial={12} laptop={1} />}
        <Col initial={12} tablet={6} laptop={6}>
          <Documents>
            {props.documents?.map((document, i) => (
              <Fragment key={i}>
                <Card type="documentLight" {...(document as DocumentCardFragment)} />
              </Fragment>
            ))}

            {isMobile && props.button && (
              <Button
                size="medium"
                color="yellow"
                href={props.button.url!}
                target={props.button.target ? "_blank" : undefined}
              >
                {props.button.title}
              </Button>
            )}
          </Documents>
        </Col>
      </div>
    </Container>
  )
})

const Content = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex flex-col gap-4 h-full justify-between items-start w-full pb-8 md:pb-0",
      "[&>div]:flex [&>div]:flex-col [&>div]:gap-4",
      className,
    )}
    {...props}
  />
))
Content.displayName = "Content"

const Documents = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("flex flex-col gap-4 w-full md:gap-2", className)} {...props} />
))
Documents.displayName = "Documents"
